/**
 * `facilitation.mode_mismatch` (§5.2 advisory; `07-session-experience.md` §2.2;
 * #256) — an INFO advisory that the chosen facilitation mode CONTRADICTS the
 * campaign's membership shape:
 *   - `solo` mode but more than one member, or
 *   - `gm` / `rotating` mode but no member holds the `gm` role (no canon source).
 *
 * Info severity (the lowest-confidence editorial tier — `advisory.ts`): a mode
 * mismatch is never corruption and NEVER refuses a write. It is a hint that the
 * GM probably wants a different posture (e.g. a campaign that grew past one
 * member is still flagged `solo`). The GM resolves it by changing the mode or
 * the membership; there is no one-click fact fix.
 *
 * The mismatch predicate is the single pure helper `facilitationModeMismatch` in
 * @dl/shared (shared with the creation-time auto-suggest), so the auditor and the
 * create flow can never diverge on "what counts as a mismatch". Identity-free: a
 * `message_key` + `params` only, no baked prose, no entity participants (this is
 * a campaign-scoped notice, not an entity-scoped one).
 *
 * Progressive disclosure: the rule is a no-op unless the caller supplies the
 * `facilitation` binding (mode + membership shape) — a campaign that has not
 * surfaced membership counts simply never fires it.
 */
import { facilitationModeMismatch } from '@dl/shared';

import type { RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** The rule id — advisory `rule` + the §5.2.1 override key. */
export const FACILITATION_MODE_MISMATCH_RULE = 'facilitation.mode_mismatch';

export function facilitationModeMismatchRule(ctx: AuditContext): RawAdvisory[] {
  const binding = ctx.facilitation;
  if (!binding) return [];

  const reason = facilitationModeMismatch({
    mode: binding.mode,
    memberCount: binding.memberCount,
    hasGmRole: binding.hasGmRole,
  });
  if (reason === null) return [];

  return [
    {
      severity: 'info',
      rule: FACILITATION_MODE_MISMATCH_RULE,
      message_key: 'auditor.facilitation.mode_mismatch',
      // `reason` discriminates the two mismatch shapes so the render boundary can
      // pick an apt message ("solo mode, multiple members" vs "gm mode, no GM").
      params: {
        mode: binding.mode,
        reason,
        member_count: binding.memberCount,
      },
      fact_ids: [],
      entity_ids: [],
    },
  ];
}
